<script setup lang="ts">
import BaseOverlay from '@/overlays/BaseOverlay.vue';
import { useInstitutionStore } from '@/stores/institution';
import { IProfile } from '@/interfaces';
import { computed, onMounted, ref } from 'vue';
import DepartmentSelectorContent from '@/components/shared/DepartmentSelectorContent.vue';
import { useDepartmentStore } from '@/stores/department';
import ProfilesGrid from '@/components/shared/ProfilesGrid.vue';
import { useProfilesStore } from '@/stores/profiles';
import DepartmentSelectorList from '@/components/shared/DepartmentSelectorList.vue';
import { InstitutionDepartment } from '@/graphql/custom-types';

const institutionStore = useInstitutionStore();
const departmentStore = useDepartmentStore();
const profilesStore = useProfilesStore();

const emit = defineEmits(['success', 'close']);
const selectedDepartment = ref<InstitutionDepartment>();
const showDepartmentsList = ref(false);

interface Props {}

withDefaults(defineProps<Props>(), {});

onMounted(() => {
  selectedDepartment.value = (institutionStore.departments || []).find(
    (department) => department.id === departmentStore.id
  );
});

const profileSelected = (profile: IProfile) => {
  emit('success', profile);
};

const getDepartmentIds = (profile: IProfile) => {
  return profile.departments.map((department) => parseInt(department.id));
};

const profiles = computed((): IProfile[] => {
  if (!selectedDepartment.value) return [];

  return profilesStore.all
    .filter((profile) =>
      getDepartmentIds(profile).includes(parseInt(selectedDepartment.value!.id))
    )
    .filter((profile: IProfile) => profile.type === 'EMPLOYEE')
    .sort((a: IProfile, b: IProfile) =>
      a.displayName.localeCompare(
        b.displayName,
        institutionStore.locale || 'da'
      )
    );
});

const departmentSelected = (department: InstitutionDepartment) => {
  selectedDepartment.value = department;
  showDepartmentsList.value = false;
};
</script>

<template>
  <BaseOverlay @close="emit('close')" :title="$t('scheduleShift.replaceShift')">
    <template #content>
      <p class="b600 mb-4">
        {{ $t('scheduleShift.replaceShiftChooseText') }}
      </p>

      <ProfilesGrid
        :profiles="profiles"
        selected-profile-type="EMPLOYEE"
        @profile-selected="profileSelected"
      />
    </template>

    <template
      #actions
      v-if="institutionStore.departments?.length && selectedDepartment"
    >
      <DepartmentSelectorContent
        :selected-department="selectedDepartment"
        @department-selected="departmentSelected"
        @click="showDepartmentsList = !showDepartmentsList"
      />

      <DepartmentSelectorList
        class="absolute"
        v-if="showDepartmentsList"
        :selected-department="selectedDepartment"
        :departments="institutionStore.departments || []"
        @department-selected="departmentSelected"
      />
    </template>
  </BaseOverlay>
</template>
